import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { AlertTriangle, Phone, MapPin, Shield, Loader2, X } from 'lucide-react';
import { toast } from 'sonner';

const EmergencySOS = ({ isOpen, onClose, rideId = null }) => {
  const [triggering, setTriggering] = useState(false);
  const [sosActive, setSosActive] = useState(false);
  const [sosId, setSosId] = useState(null);
  const [location, setLocation] = useState(null);
  const [countdown, setCountdown] = useState(null);

  useEffect(() => {
    if (isOpen && !location) {
      getCurrentLocation();
    }
  }, [isOpen]);

  useEffect(() => {
    let timer;
    if (countdown !== null && countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    } else if (countdown === 0) {
      triggerSOS();
    }
    return () => clearTimeout(timer);
  }, [countdown]);

  const getCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude
          });
        },
        (error) => {
          console.error('Location error:', error);
          setLocation({ lat: 0, lng: 0 });
        }
      );
    }
  };

  const startCountdown = () => {
    setCountdown(5);
  };

  const cancelCountdown = () => {
    setCountdown(null);
  };

  const triggerSOS = async () => {
    setTriggering(true);
    try {
      const token = localStorage.getItem('reddi_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/emergency/sos`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ride_id: rideId,
          location: location || { lat: 0, lng: 0 },
          message: 'Emergency SOS triggered'
        })
      });

      const data = await response.json();
      setSosId(data.sos_id);
      setSosActive(true);
      toast.error('Emergency alert sent! Help is on the way.', { duration: 10000 });
    } catch (error) {
      console.error('Error triggering SOS:', error);
      toast.error('Failed to send alert. Please call 911 directly.');
    } finally {
      setTriggering(false);
      setCountdown(null);
    }
  };

  const resolveSOS = async () => {
    if (!sosId) return;
    
    try {
      const token = localStorage.getItem('reddi_token');
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/emergency/sos/${sosId}/resolve`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      setSosActive(false);
      setSosId(null);
      toast.success('Emergency alert resolved');
      onClose();
    } catch (error) {
      console.error('Error resolving SOS:', error);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md bg-slate-900 border-red-600 border-2">
        <DialogHeader>
          <DialogTitle className="text-white font-display uppercase tracking-tight flex items-center gap-2">
            <Shield className="w-6 h-6 text-red-600" />
            Emergency SOS
          </DialogTitle>
        </DialogHeader>

        <div className="py-6">
          {sosActive ? (
            // SOS Active State
            <div className="text-center">
              <div className="w-20 h-20 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse">
                <AlertTriangle className="w-10 h-10 text-white" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Help is on the way!</h3>
              <p className="text-slate-400 mb-6">
                Stay calm and keep your phone accessible.
              </p>
              
              <div className="p-4 bg-slate-800 rounded-xl mb-6">
                <p className="text-slate-400 text-sm mb-2">Emergency Support</p>
                <a href="tel:1-800-REDDI-SOS" className="flex items-center justify-center gap-2 text-xl font-bold text-white">
                  <Phone className="w-5 h-5 text-green-500" />
                  1-800-REDDI-SOS
                </a>
              </div>

              <Button
                onClick={resolveSOS}
                variant="outline"
                className="w-full border-green-600 text-green-500 hover:bg-green-600/20"
              >
                I'm Safe - Cancel Alert
              </Button>
            </div>
          ) : countdown !== null ? (
            // Countdown State
            <div className="text-center">
              <div className="w-24 h-24 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-5xl font-display font-bold text-white">{countdown}</span>
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Sending SOS in {countdown}...</h3>
              <p className="text-slate-400 mb-6">
                Tap cancel if this was a mistake
              </p>
              <Button
                onClick={cancelCountdown}
                variant="outline"
                className="w-full border-slate-600 text-slate-300 hover:bg-slate-800"
              >
                <X className="w-4 h-4 mr-2" />
                Cancel
              </Button>
            </div>
          ) : (
            // Initial State
            <div className="text-center">
              <div className="w-20 h-20 bg-red-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertTriangle className="w-10 h-10 text-red-600" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Are you in danger?</h3>
              <p className="text-slate-400 mb-6">
                This will alert Reddi support and share your location.
              </p>

              {location && (
                <div className="flex items-center justify-center gap-2 text-slate-400 text-sm mb-6">
                  <MapPin className="w-4 h-4" />
                  <span>Location: {location.lat.toFixed(4)}, {location.lng.toFixed(4)}</span>
                </div>
              )}

              <Button
                onClick={startCountdown}
                disabled={triggering}
                className="w-full h-16 rounded-full bg-red-600 hover:bg-red-700 text-white font-bold text-lg mb-4"
                data-testid="trigger-sos-btn"
              >
                {triggering ? (
                  <Loader2 className="w-6 h-6 animate-spin" />
                ) : (
                  <>
                    <AlertTriangle className="w-5 h-5 mr-2" />
                    Send Emergency Alert
                  </>
                )}
              </Button>

              <div className="grid grid-cols-2 gap-3">
                <a
                  href="tel:911"
                  className="flex items-center justify-center gap-2 p-4 bg-slate-800 rounded-xl text-white hover:bg-slate-700 transition-colors"
                >
                  <Phone className="w-5 h-5 text-green-500" />
                  <span>Call 911</span>
                </a>
                <a
                  href="tel:1-800-REDDI-SOS"
                  className="flex items-center justify-center gap-2 p-4 bg-slate-800 rounded-xl text-white hover:bg-slate-700 transition-colors"
                >
                  <Phone className="w-5 h-5 text-red-500" />
                  <span>Reddi Support</span>
                </a>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EmergencySOS;
